import { useState } from 'react';
import {
  addProductToList,
  addToSaved,
  getUserListsPreview,
} from '@/services/slaydAPI';
import useAuthBottomSheetStore from '@/stores/authBottomSheetStore';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useToastStore from '@/stores/ToastStore';
import { isUserSigned } from '@/utils/getUserInfo';

interface UseLikeHandlerOptions {
  animationDuration?: number;
  initialLiked?: boolean;
  enableSlaylistSelector?: boolean;
  productId?: number;
  productImage?: string;
}

interface UseLikeHandlerReturn {
  isLiked: boolean;
  animateHeart: boolean;
  handleLikeClick: (e?: React.MouseEvent) => Promise<void>;
  closeSelector?: () => void;
}

export const useLikeHandler = ({
  initialLiked = false,
  enableSlaylistSelector = true,
  productId,
  productImage,
}: UseLikeHandlerOptions = {}): UseLikeHandlerReturn => {
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [animateHeart, setAnimateHeart] = useState(false);
  const openSheet = useAuthBottomSheetStore((state) => state.openSheet);

  const {
    openSlaylistSelector,
    closeSlaylistSelector,
    setProductId,
    getSelectedSlaylist,
    setProductSlaylist,
    removeProductSlaylist,
    getSlaylistIdbyName,
    setSlaylists,
  } = useSlaylistSelectorStore();

  const { showToast } = useToastStore();

  const updateSlaylists = (): Promise<void> => {
    const fetchAndSetSlaylists = async (): Promise<void> => {
      try {
        const slaylists = await getUserListsPreview();
        setSlaylists(slaylists || []);
      } catch (error) {
        console.error('Error fetching slaylists:', error);
      }
    };

    // RETURN the promise here
    return fetchAndSetSlaylists();
  };

  const handleLikeClick = async (e?: React.MouseEvent): Promise<void> => {
    e?.stopPropagation();
    e?.preventDefault();

    const authResult = isUserSigned();

    if (authResult) {
      if (!productId) return;
      setAnimateHeart(true);
      const savedListId = getSlaylistIdbyName('Saved');
      setProductSlaylist(productId, 'Saved', Number(savedListId));
      const selectedSlaylist = getSelectedSlaylist();
      const selectedListId = getSlaylistIdbyName(selectedSlaylist);
      if (selectedSlaylist) {
        try {
          toggleLike();
          setProductSlaylist(
            productId,
            selectedSlaylist,
            Number(selectedListId)
          );
          showToast(`Added to '${selectedSlaylist}'`, {
            text: 'CHANGE',
            action: {
              type: 'change',
              productId: Number(productId),
              productImage: productImage || undefined,
            },
          });
          setAnimateHeart(true);

          // Make the API call
          await addProductToList(productId, selectedSlaylist);
        } catch (error) {
          console.error('Failed to add product to list:', error);

          removeProductSlaylist(productId);
          showToast('Failed to add product to list');
          setIsLiked(!isLiked);
        }
      } else {
        openSlaylistSelector({ productId, productImage });
        await addToSaved(productId);
        setProductId(productId);
      }
    } else {
      openSheet(
        async (_isSignUp, _name) => {
          await updateSlaylists();
          handleLikeClick();
        },
        {
          signUp: (name: string) => `Welcome, ${name}!`,
          signIn: (name: string) => `Welcome back, ${name}!`,
        }
      );
    }

    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  const toggleLike = (): void => {
    setIsLiked(!isLiked);
  };

  const closeSelector = (): void => {
    closeSlaylistSelector();
  };

  return {
    isLiked,
    animateHeart,
    handleLikeClick,
    closeSelector: enableSlaylistSelector ? closeSelector : undefined,
  };
};
